import type { AssignedAccountUser, AssignedTeamSummary, TeamListItem, TeamUser } from "../types/teams.types";

export type PartitionAccountUsersResult = {
    assigned: AssignedAccountUser[];
    unassigned: TeamUser[];
    assignedCount: number;
    unassignedCount: number;
    totalCount: number;
};

const mergeUserProfile = (accountUser: TeamUser, teamListUser?: TeamUser): TeamUser => {
    if (!teamListUser) {
        return accountUser;
    }

    return {
        userId: accountUser.userId,
        name: accountUser.name ?? teamListUser.name,
        email: accountUser.email ?? teamListUser.email,
        jobTitle: accountUser.jobTitle ?? teamListUser.jobTitle,
        country: accountUser.country ?? teamListUser.country,
    };
};

export const partitionAccountUsers = (
    teams: readonly TeamListItem[],
    accountUsers: readonly TeamUser[],
): PartitionAccountUsersResult => {
    const teamsByUserId = new Map<number, AssignedTeamSummary[]>();
    const profileByUserId = new Map<number, TeamUser>();

    teams.forEach((team) => {
        team.users.forEach((user) => {
            const existingTeams = teamsByUserId.get(user.userId) ?? [];
            existingTeams.push({ teamId: team.teamId, name: team.name });
            teamsByUserId.set(user.userId, existingTeams);
            if (!profileByUserId.has(user.userId)) {
                profileByUserId.set(user.userId, user);
            }
        });
    });

    const assignedIds = new Set(teamsByUserId.keys());
    const assigned: AssignedAccountUser[] = [];
    const unassigned: TeamUser[] = [];

    accountUsers.forEach((accountUser) => {
        if (assignedIds.has(accountUser.userId)) {
            assigned.push({
                ...mergeUserProfile(accountUser, profileByUserId.get(accountUser.userId)),
                teams: teamsByUserId.get(accountUser.userId) ?? [],
            });
            return;
        }
        unassigned.push(accountUser);
    });

    return {
        assigned,
        unassigned,
        assignedCount: assigned.length,
        unassignedCount: unassigned.length,
        totalCount: accountUsers.length,
    };
};
